<template>
  <div class="ApplicationReview">
    <header class="reviewHeading">
      <p>
        Please check through the details of this application carefully. If you
        need to amend anything, please do so before you submit.
      </p>

      <div v-if="alerts.length > 0 && !hasAttemptToSubmit" class="message">
        <font-awesome-icon
          focusable="false"
          class="icon"
          icon="exclamation-circle"
        />Your application is incomplete. Please review the highlighted
        sections.
      </div>
      <div v-else-if="alerts.length > 0 && hasAttemptToSubmit" class="message">
        <font-awesome-icon
          focusable="false"
          class="icon"
          icon="exclamation-circle"
        />There was a problem submitting your application. Please review the
        highlighted sections.
      </div>
    </header>
    <AppMessage
      v-if="isSectionDisabled('emergency')"
      message="Please note that section 7 'Emergency contacts' has not been enabled. In order to view the contents of this section, you need to complete and save section 6 'Parents/carers'."
      type="warning"
    />

    <ApplicationReviewSection
      v-for="section in sections.filter((section) => section.id !== 'review')"
      :key="section.id"
      :section="section"
      :fields="section.fields"
      :alerts="alerts"
    />

    <div class="reviewDeclaration__container">
      <div class="reviewDeclaration white-card">
        <h2 class="reviewDeclaration__heading">Declaration</h2>
        <section class="reviewDeclaration__terms">
          <p>
            I acknowledge that upon review of my financial emergency assistance application, additional proofs/documents may or may not be requested of me.
          </p>
          <p>
            I authorize the Building Blocks of Islam to release/receive my information to/from any person/agencies that need to be involved for the purpose of processing my case. Information can be, but is not limited to, my application, ID, proof of income/asset/rent, and all of the documents that I have provided to the Building Blocks of Islam's Online Emergency Assistance program.  
          </p>
          <p>
            I understand that the information shared between different persons and/or agencies is only used to the extent of processing my case and shared only by the authorized staff that need it to process my case.
          </p>
          <p>
             I also acknowledge that sharing information between different persons and/or agencies does not guarantee any help from the Building Blocks’ Online Emergency Assistance program.
          </p>
          <p>
           I also understand that I may cancel this authorization at any time through a written request, which may result in a denial of my case.
          </p>
        </section>
        <section class="reviewDeclaration__declaration">
          <p class="reviewDeclaration__declaration-heading">
            Please complete the declaration below:
          </p>

          <AppCheckbox
            :value="declarationAccepted"
            :trueValue="true"
            :falseValue="false"
            @change="toggleDeclarer"
            :disabled="!curDeclarer"
            :vHtmlLabel="declarationText"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import ApplicationReviewSection from '@/components/application/ApplicationReviewSection.vue'
import AppMessage from '@/components/app/AppMessage.vue'
import AppCheckbox from '@/components/app/AppCheckbox'

export default {
  name: 'ApplicationReview',
  components: {
    ApplicationReviewSection,
    AppMessage,
    AppCheckbox
  },
  props: [
    'declarer',
    'sections',
    'fields',
    'alerts',
    'declarerName',
    'hasAttemptToSubmit'
  ],
  data() {
    return {
      curDeclarer: this.declarer
    }
  },
  computed: {
    declarationAccepted() {
      return (
        this.curDeclarer &&
        this.$store.state.application.clientName_1 ===
          this.declarer
      )
    },
    declarerFullName() {
      return (
        this.curDeclarer &&
        `${this.curDeclarer.firstName} ${
          this.curDeclarer.lastName
        }`.trim()
      )
    },
    declarationText() {
      const declarerText =
        this.curDeclarer &&
        `, <strong> ${this.declarerFullName} </strong> of <strong>${
          this.curDeclarer.email
        }</strong>`

      return `<span class="reviewDeclaration__declaration-text">
            I${this.curDeclarer ? declarerText : ''} declare that the
            information provided in this application is, to the best of my
            knowledge and belief, accurate and complete.

          </span>`
    }
  },
  methods: {
    isSectionDisabled(id) {
      this.$emit('isSectionDisabled', { id })
    },
     isSectionVisible(section) {
      this.$emit('isSectionVisible', { section })
    },
    toggleDeclarer(value) {
      if (value) {
        this.$emit(
          'setFinalDeclarer',
          `${this.curDeclarer.firstName} ${
          this.curDeclarer.lastName
        }`
        )
      } else {
        this.$emit('clearFinalDeclarer')
      }
    }
  }
}
</script>
<style scoped lang="scss">
.ApplicationReview {
  padding: $content-padding;

  word-wrap: break-word;
  overflow-wrap: break-word;

  .message {
    display: flex;
    margin: $content-padding 0;
    padding: $content-padding;
    border: 1px solid $color-red;
    border-radius: $field-border-radius;
    background-color: $color-red-lighter;
    font-size: 1.6em;

    .icon {
      margin-right: 0.6em;
      margin-top: 0.2em;
      color: $color-red;
      font-size: 2.5rem;
    }
  }

  .reviewHeading {
    padding: $content-padding;

    h2 {
      margin: 0;
      padding-top: $content-padding * 6;
      padding-bottom: 1em;
      font-weight: $text-bold;
      font-size: $text-h2;
    }

    p {
      font-size: $text-h5;
    }
    .small {
      font-size: $text-small;
    }
  }

  .section {
    padding: $content-padding;
  }

  .fields {
    padding: 0 0 $content-padding;
  }

  .reviewDeclaration {
    padding: 2.5rem 4rem 5rem 4rem;

    &__container {
      padding: 1rem;
      margin-top: 2rem;
    }

    &__declaration-heading {
      margin-top: 3rem;
      color: $color-primary;
      font-size: 1.6rem;
      font-weight: bold;
    }

    &__heading {
      font-weight: $text-bold;
      margin-bottom: 2rem;
    }

    &__terms p {
      font-size: 1.2rem;
      line-height: 2rem;
      color: $color-grey-dark-70;
    }

    & ::v-deep {
      .reviewDeclaration__declaration-text {
        font-size: 1.6rem;
        line-height: 2.7rem;
        color: $color-primary;
      }

      .AppCheckbox {
        margin-top: 1rem;
      }

      .AppCheckbox span::before,
      .AppCheckbox span::after,
      input {
        left: 0;
        top: 9px;
      }
    }
  }
}
</style>
